import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { OrganisationService } from "@services/organisation/organisation.service";
import { TenantService } from "@services/tenant/tenant.service";
import { UrlBuilder } from "@utilities/url-builder";
import { BehaviorSubject, firstValueFrom, Observable } from "rxjs";
import { LegalEntitySwitcherItem } from "src/types/legal-entity";
import { Organisation } from "src/types/organisation";

const LOCAL_STORAGE_ORGANISATION_ID_NAME = 'organisationId';

/**
 * A service that provides uncategorised opeartions related to the current user
 */
@Injectable({
    providedIn: 'root'
})
export class UserService {
    private readonly organisation: BehaviorSubject<Organisation | undefined> = new BehaviorSubject<Organisation | undefined>(undefined);

    /**
     * Emits the currently selected Legal Entity whenever it changes, so views showing
     * entity-scoped data can refresh themselves instead of going stale after a switch.
     */
    public get organisation$(): Observable<Organisation | undefined> {
        return this.organisation.asObservable();
    }

    constructor(
        private readonly organisationService: OrganisationService,
        private readonly tenantService: TenantService,
        private readonly http: HttpClient
    ) { }

    public async getAvailableLegalEntities(): Promise<LegalEntitySwitcherItem[]> {
        const url = (await this.getBaseUrl()).addResource('organisations').build();
        return await firstValueFrom(this.http.get<LegalEntitySwitcherItem[]>(url));
    }

    /**
     * Gets the selected organisation.
     * @returns The selected organisation, if one is selected.
     */
    public async getSelectedLegalEntity(): Promise<Organisation | undefined> {
        const value = this.organisation.getValue();
        if (value) { return value; }

        const organisationId = localStorage.getItem(LOCAL_STORAGE_ORGANISATION_ID_NAME);
        if (!organisationId) { return; }

        return await this.setSelectedLegalEntityFromApi(organisationId);
    }

    /**
     * Sets the selected legal entity.
     * @param organisationId The id of the Legal Entity to select.
     */
    public async setSelectedLegalEntity(organisationId: string): Promise<void> {
        localStorage.setItem(LOCAL_STORAGE_ORGANISATION_ID_NAME, organisationId);
        await this.setSelectedLegalEntityFromApi(organisationId);
    }

    /**
     * Clears the selected organisation.
     */
    public clearSelectedLegalEntity(): void {
      localStorage.removeItem(LOCAL_STORAGE_ORGANISATION_ID_NAME);
      this.organisation.next(undefined);
      console.log('Cleared selected legal entity');
    }

    private async setSelectedLegalEntityFromApi(organisationId: string): Promise<Organisation> {
        const org = await this.organisationService.get(organisationId);
        this.organisation.next(org);
        return org;
    }

    private async getBaseUrl(): Promise<UrlBuilder> {
        return new UrlBuilder(await this.tenantService.getApiUrl()).addResource('user');
    }
}